import { EventRegistry } from './event_registry';

/**
 * Will remember the last arguments the event was invoked with.
 * Any newly registering listener will immediately get called with the
 * last received event arguments.
 */
export class CachedEventRegistry<T extends any[] = any[]> extends EventRegistry<T> {
    _lastArguments;

    constructor(...initialArgs: T | []) {
        super();
        // because you can notify listeners with nothing and still track that as the last arguments,
        // we only track the last args here if you've actually provided something.
        this._lastArguments = initialArgs.length ? initialArgs : undefined;
    }

    /**
     * Add an event listener function
     * @param listenerFunc
     * @returns {function()} A function to remove the event listener from the registry.
     */
    addEventListener(listenerFunc: (...args: T) => any): () => void {
        // we check if last args is truthy to trigger a callback for the new listener.
        // meaning even if you notify listeners with NOTHING, we'll still track that as the
        // last args, and call any subsequent listeners immediately here.
        if (this._lastArguments) {
            try {
                listenerFunc.apply(this, this._lastArguments);
            } catch (e) {
                console.error(
                    'Cached event register error while doing initial listener callback',
                    e,
                );
            }
        }
        return super.addEventListener(listenerFunc);
    }

    /**
     * Notify all of the registered event listeners. Any passed in
     * arguments will be sent to the listeners
     * @returns A promise with the results from calling all of the event handlers
     */
    notifyListeners(...args: T): Promise<any> {
        const result = Promise.all(
            [...this._listeners].map((l) => {
                // If one of the listeners throws an uncaught exception we can get strange behaviour
                // which is hard to trace. Catching here is nice to solve the traceability issue.
                // This is especially true with CachedEventRegistry, as this._lastArguments is not
                // saved in the case of an exception!
                try {
                    return l.apply(this, args);
                } catch (e) {
                    console.error('Cached event register error while doing listener callback', e);
                    return null;
                }
            }),
        );
        this._lastArguments = args;
        return result;
    }

    /**
     * Sometimes you want to clear the cache so new subscribers don't get the last call.
     * Use this function for that case!
     */
    clearCache(): void {
        this._lastArguments = undefined;
    }
}
